import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { scrollTop } from '@share/utils';
import { Routes } from '@share/constants';

import './style.scss';

interface RouteParams {
  accountUsername: string;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

const CampingsFAQsPage: React.FC = () => {
  useEffect(() => {
    scrollTop();
  }, []);
  const history = useHistory();
  const params = useParams<RouteParams>();
  const { accountUsername } = params;
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const navigateToWelcome = () => {
    history.push(`/${accountUsername}${Routes.CampingsWelcome}`);
  };

  const navigateToCampings = () => {
    history.push(`/${accountUsername}${Routes.Campings}`);
  };

  const navigateToBookingHowTo = () => {
    history.push(`/${accountUsername}${Routes.CampingsBookingHowTo}`);
  };

  // Arrow Left Icon
  const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );

  const faqs: FAQItem[] = [
    {
      id: '1',
      question: 'What is AOR/UCN?',
      answer: 'AOR/UCN is the Adventure Outdoor Resorts / Unlimited Camping Network platform inside RSI Vacations. It connects members with members-only campground offers redeemable via promo codes directly on campground websites (or by phone if no online booking).',
    },
    {
      id: '2',
      question: "What's the difference between Gold and Platinum?",
      answer: (
        <>
          <p style={{ marginBottom: '12px' }}>
            <strong>Gold</strong> — Access to the AOR/UCN camping platform: campground listings, members-only deals, and promo codes.
          </p>
          <p>
            <strong>Platinum</strong> — Everything in Gold plus RSI Vacations travel benefits: hotels, condos, cruises, curated getaways, and full concierge services.
          </p>
        </>
      ),
    },
    {
      id: '3',
      question: 'How do I see deals?',
      answer: 'Log in at AORUCN.com → Go to Login, choose Gold or Platinum → pick State/Region → view campground cards with amenities, map, deal text, promo code, and booking link (or phone).',
    },
    {
      id: '4',
      question: 'Do I have to call AOR/UCN to book?',
      answer: 'No. All bookings are direct: click the campground link and apply your promo code at checkout. If the campground doesn\'t have online booking, call their number and mention the code.',
    },
    {
      id: '5',
      question: 'Are promo codes public?',
      answer: 'No. Codes are members-only and shown behind login. Sharing codes outside the club violates our rules (see "Rate Integrity & Misuse" below).',
    },
    {
      id: '6',
      question: 'How often do codes change?',
      answer: 'Codes may change without notice to protect campground rate integrity. Always pull the latest code from your dashboard right before booking.',
    },
    {
      id: '7',
      question: 'What if my code doesn\'t work?',
      answer: 'Check eligibility/rules → confirm exact code → try an alternate browser → if still stuck, contact us with a screenshot.',
    },
    {
      id: '8',
      question: 'Can I stack the code with other discounts?',
      answer: 'Unless the campground listing says otherwise, no stacking (AAA, military, loyalty, etc.). The listing will specify if stacking is allowed.',
    },
    {
      id: '9',
      question: 'Is there a limit to nights or site types?',
      answer: 'Each campground sets its own blackout dates, minimum nights, site types, and caps. These appear on the campground card as "Deal Rules."',
    },
    {
      id: '10',
      question: 'How do cancellations/changes work?',
      answer: 'All cancellations/changes follow the campground\'s own policy (posted on their site or on your confirmation). AOR/UCN is not the merchant of record.',
    },
    {
      id: '11',
      question: 'Who do I pay?',
      answer: 'You pay the campground directly (website or phone). AOR/UCN does not collect payment.',
    },
    {
      id: '12',
      question: 'Is customer support available?',
      answer: 'Yes. For website access, code visibility, or listing errors, contact AOR/UCN Support in your portal. For site availability, arrivals, park rules, billing, or refunds, contact the campground.',
    },
  ];

  const rules = [
    {
      id: '1',
      title: '1) Closed-User Group (CUG)',
      items: [
        'All benefit details and promo codes are accessible only behind login.',
        'Do not share codes publicly (social media, forums, etc.).',
      ],
    },
    {
      id: '2',
      title: '2) Rate Integrity & Misuse',
      items: [
        'Codes are unique to AOR/UCN and may be rotated without notice.',
        'Misuse (sharing codes with non-members, reselling access, or posting publicly) can result in member removal from AOR/UCN and/or loss of eligibility for future benefits.',
      ],
    },
    {
      id: '3',
      title: '3) Booking Protocol',
      items: [
        'Retrieve codes at time of booking to ensure the newest code is used.',
        'Apply the code at the campground\'s checkout or mention it by phone to the campground.',
        'Respect each park\'s arrival rules, occupancy limits, and site policies.',
      ],
    },
    {
      id: '4',
      title: '4) Deal Rules Display',
      items: [
        'Every listing shows what\'s included (e.g., % off nightly rate, 3rd night free, fee waiver).',
        'Every listing shows restrictions (site types, dates, minimum nights, resident/age limits, pets, rig length, etc.).',
        'Stacking, credit, or rainchecks are not guaranteed unless the listing says so.',
      ],
    },
    {
      id: '5',
      title: '5) Identity Verification',
      items: [
        'A campground may verify active membership (e.g., last name + code + confirmation). Please carry your photo ID.',
      ],
    },
    {
      id: '6',
      title: '6) Privacy & Payments',
      items: [
        'Payments are handled by the campground\'s processor. AOR/UCN does not store your payment details.',
        'See our Privacy Notice (portal) for data use related to your account access and clicks (e.g., for fraud prevention and analytics).',
      ],
    },
  ];

  return (
    <div className="campings-faqs-page">
      <div className="campings-faqs">
        <div className="container">
          <div className="campings-faqs__content">
            {/* Back Button */}
            <button className="campings-faqs__back-button" onClick={navigateToWelcome}>
              <ArrowLeftIcon />
              Back to Home
            </button>

            {/* Page Title */}
            <div className="campings-faqs__header">
              <h1 className="campings-faqs__title">Member FAQ</h1>
              <p className="campings-faqs__subtitle">Gold & Platinum membership questions answered</p>
            </div>

            {/* Member FAQ Section */}
            <div className="campings-faqs__section">
              <h2 className="campings-faqs__section-title">Frequently Asked Questions</h2>
              <div className="campings-faqs__accordion">
                {faqs.map((faq) => (
                  <div 
                    key={faq.id} 
                    className={`campings-faqs__item ${openFAQ === faq.id ? 'open' : ''}`}
                  >
                    <div 
                      className="campings-faqs__question"
                      onClick={() => toggleFAQ(faq.id)}
                    >
                      <span className="campings-faqs__question-text">{faq.question}</span>
                      <span className="campings-faqs__toggle">
                        {openFAQ === faq.id ? '−' : '+'}
                      </span>
                    </div>
                    {openFAQ === faq.id && (
                      <div className="campings-faqs__answer">
                        {typeof faq.answer === 'string' ? <p>{faq.answer}</p> : faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Rules & Protocols Section */}
            <div className="campings-faqs__section">
              <h2 className="campings-faqs__section-title">Rules & Protocols</h2>
              <div className="campings-faqs__rules">
                {rules.map((rule) => (
                  <div key={rule.id} className="campings-faqs__rule-card">
                    <h3 className="campings-faqs__rule-title">{rule.title}</h3>
                    <ul className="campings-faqs__rule-list">
                      {rule.items.map((item, index) => (
                        <li key={index} className="campings-faqs__rule-item">
                          <span className="campings-faqs__rule-bullet">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="campings-faqs__cta">
              <h3 className="campings-faqs__cta-title">Still have questions?</h3>
              <p className="campings-faqs__cta-text">
                Contact our support team or review our booking guide for more details.
              </p>
              <div className="campings-faqs__cta-buttons">
                <button 
                  className="campings-faqs__button campings-faqs__button--primary"
                  onClick={navigateToCampings}
                >
                  Browse Campgrounds
                </button>
                <button 
                  className="campings-faqs__button campings-faqs__button--secondary"
                  onClick={navigateToBookingHowTo}
                >
                  View Booking Guide
                </button>
                <button 
                  className="campings-faqs__button campings-faqs__button--outline"
                  onClick={navigateToWelcome}
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="campings-faqs__footer">
          <div className="container">
            <div className="campings-faqs__footer-content">
              <p className="campings-faqs__footer-text">
                &copy; 2025 AOR/UCN by RSI Vacations. All rights reserved.
              </p>
              <div className="campings-faqs__footer-links">
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="campings-faqs__footer-link">FAQs</button>
                <button onClick={navigateToBookingHowTo} className="campings-faqs__footer-link">Booking Guide</button>
                <a href="#" className="campings-faqs__footer-link">Privacy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export { CampingsFAQsPage };
